import {
  PlatformContextType,
  UserAccountType
} from "@wingspanhq/users/dist/lib/interfaces";
import { IS_LOCAL_ENV } from "../../shared/constants/environment";
import { selectorIsTaxOnlyPayeeContext } from "./selectors/selectorIsTaxOnlyPayeeContext";
import { selectorIsTaxOnlyPayerContext } from "./selectors/selectorIsTaxOnlyPayerContext";
import { DashboardFocusedFeatureContext } from "./useGetDashboardFocusedFeaturesContext";

export type CreateDashboardFocusedFeature = (
  context: DashboardFocusedFeatureContext
) => boolean;

export const DashboardFocusedFeaturesConfig = {
  // showTaxOnlyPayeeNavigation: (context: DashboardFocusedFeatureContext) => {
  //   return selectorIsTaxOnlyPayeeContext(context);
  // },
  showContractorDetailsTaxOnlyPayerEligibilityWarning: (
    context: DashboardFocusedFeatureContext
  ) => {
    return selectorIsTaxOnlyPayerContext(context);
  },
  showSideNavSwitchAccount: (context: DashboardFocusedFeatureContext) => {
    return !context.session.isImpersonated;
  },
  showSideNavNew: (context: DashboardFocusedFeatureContext) => {
    return (
      !selectorIsTaxOnlyPayeeContext(context) &&
      !selectorIsTaxOnlyPayerContext(context)
    );
  },
  showSideNavGettingStarted: (context: DashboardFocusedFeatureContext) => {
    //         isVisible={!is1099OnlyRecipient}
    return true;
  },
  // "Get paid" section — visible when any of its items (Invoices, Clients) is.
  showSideNavGetPaid: (context: DashboardFocusedFeatureContext) => {
    return (
      context.hardcoded.showGetPaidNav &&
      !selectorIsTaxOnlyPayeeContext(context) &&
      (context.access.hasScope("payments.invoice") ||
        context.access.hasScope("payments.payer"))
    );
  },
  // "Get paid" > Invoices
  showSideNavGetPaidInvoices: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("payments.invoice");
  },
  // "Get paid" > Clients (payers)
  showSideNavGetPaidClients: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("payments.payer");
  },
  // "Send payments" section — visible when any of its items is.
  showSideNavSendPayments: (context: DashboardFocusedFeatureContext) => {
    return (
      context.hardcoded.showSendPaymentsNav &&
      !selectorIsTaxOnlyPayeeContext(context) &&
      (context.access.hasScope("payments.payable") ||
        context.access.hasScope("payments.payee") ||
        context.access.hasScope("payments.engagement") ||
        context.access.hasScope("payments.requirementDefinition"))
    );
  },
  showSideNavPayables: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("payments.payable");
  },
  showSideNavPayees: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("payments.payee");
  },
  showSideNavWorkLogsPayee: (context: DashboardFocusedFeatureContext) => {
    return (
      context.user.payersWithWorkLoggingCount > 0 &&
      context.access.hasScope("payments.workLog")
    );
  },
  showSideNavWorkLogsPayer: (context: DashboardFocusedFeatureContext) => {
    return (
      context.user.workDefinitionsCount > 0 &&
      context.access.hasScope("payments.workDefinition")
    );
  },
  showSideNavTaxFillingSection: (context: DashboardFocusedFeatureContext) => {
    const isUSUser = context.user.country === "US";

    const currentCondition =
      context.access.hasScope("payments.taxForm") &&
      context.experiments.nec1099season2024 &&
      (context.user.contexts.some(
        c =>
          c === PlatformContextType.TaxOnlyPayer ||
          c === PlatformContextType.Payer
      ) ||
        context.user.userType === UserAccountType.enterprise);

    const condition2023 =
      context.access.hasScope("payments.taxForm") &&
      context.user.userType === UserAccountType.enterprise;

    return isUSUser && (currentCondition || condition2023);
  },
  // "Financial benefits" section — visible when any of its items is.
  showSideNavFinancialBenefits: (context: DashboardFocusedFeatureContext) => {
    return (
      !selectorIsTaxOnlyPayeeContext(context) &&
      (context.access.hasScope("bookkeeping.businessBankingAccount") ||
        context.access.hasScope("bookkeeping.transaction") ||
        context.access.hasScope("bookkeeping.withholdingTransaction"))
    );
  },
  // "Financial benefits" > Wingspan wallet
  showSideNavWallet: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("bookkeeping.businessBankingAccount");
  },
  // "Financial benefits" > Income & expenses
  showSideNavIncomeAndExpenses: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("bookkeeping.transaction");
  },
  // "Financial benefits" > Tax withholdings
  showSideNavTaxWithholdings: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("bookkeeping.withholdingTransaction");
  },
  showSideNavLifestyleBenefits: (context: DashboardFocusedFeatureContext) => {
    // TODO: only show this in local env
    if (IS_LOCAL_ENV) {
      return true;
    }

    return (
      context.access.hasScope("*") &&
      context.user.userType !== UserAccountType.enterprise
    );
  },
  showSideNavReports: (context: DashboardFocusedFeatureContext) => {
    return (
      !selectorIsTaxOnlyPayeeContext(context) &&
      context.experiments.reports &&
      (context.access.hasScope("payments.payable") ||
        context.access.hasScope("payments.payee"))
    );
  },
  showSideNavDataAndIntegrations: (context: DashboardFocusedFeatureContext) => {
    return (
      context.access.hasScope("files.files") ||
      context.access.hasScope("payments.payable") ||
      context.access.hasScope("payments.customField")
    );
  },
  showSideNavDocuments: (context: DashboardFocusedFeatureContext) => {
    return (
      context.access.hasScope("files.files") &&
      !context.experiments.masterPolicyEnrollment
    );
  },
  showSideNavMyResourcesSettings: (context: DashboardFocusedFeatureContext) => {
    return (
      context.experiments.masterPolicyEnrollment &&
      context.access.hasScope("files.files")
    );
  },
  showSideNavCustomFields: (context: DashboardFocusedFeatureContext) => {
    return (
      context.access.hasScope("payments.customField") &&
      context.hardcoded.showCustomFieldsNav
    );
  },
  showSideNavButchImports: (context: DashboardFocusedFeatureContext) => {
    return (
      (context.access.hasScope("payments.payable") ||
        context.access.hasScope("payments.payee")) &&
      context.hardcoded.showBatchImportsNav
    );
  },
  showSideNavIntegrations: (context: DashboardFocusedFeatureContext) => {
    return (
      context.access.hasScope("*") &&
      context.experiments.qboOnboarding &&
      context.user.userType === UserAccountType.enterprise
    );
  },
  showSideNavTokens: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("*");
  },
  showSideNavOrganizationAccounts: (
    context: DashboardFocusedFeatureContext
  ) => {
    return (
      context.access.hasScope("*") &&
      context.user.userType === UserAccountType.enterprise
    );
  },
  showSideNavSettings: (context: DashboardFocusedFeatureContext) => {
    return (
      context.access.hasScope("*") ||
      context.access.hasScope("payments.payoutSettings") ||
      context.access.hasScope("payments.paymentSettings") ||
      context.access.hasScope("payments.payrollSettings")
    );
  },
  showSideNavAccountSettings: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("*");
  },
  showSideNavPayoutMethodSettings: (
    context: DashboardFocusedFeatureContext
  ) => {
    return (
      context.access.hasScope("payments.payoutSettings") &&
      context.hardcoded.showPayoutNav
    );
  },
  showSideNavPaymentMethodSettings: (
    context: DashboardFocusedFeatureContext
  ) => {
    return (
      context.access.hasScope("payments.paymentSettings") &&
      context.hardcoded.showPaymentNav
    );
  },
  showSideNavPayoutAndPaymentsMethodSettings: (
    context: DashboardFocusedFeatureContext
  ) => {
    return (
      context.access.hasScope("payments.payoutSettings") &&
      !context.hardcoded.showPayoutNav &&
      !context.hardcoded.showPaymentNav
    );
  },
  showSideNavSendPaymentsSettings: (
    context: DashboardFocusedFeatureContext
  ) => {
    return (
      (context.access.hasScope("payments.payrollSettings") ||
        context.access.hasScope("*")) &&
      context.hardcoded.showPayableAndPayrollSettingsNav
    );
  },
  showSideNavWorkSiteSettings: (context: DashboardFocusedFeatureContext) => {
    // Work sites are part of the employer payroll surface, so they follow the
    // same gate as the other send-payments settings.
    return (
      (context.access.hasScope("payments.payrollSettings") ||
        context.access.hasScope("*")) &&
      context.hardcoded.showPayableAndPayrollSettingsNav
    );
  },
  showSideNavEngagementRequirements: (
    context: DashboardFocusedFeatureContext
  ) => {
    return true;
    // return (
    //   (context.access.hasScope("payments.engagement") ||
    //     context.access.hasScope("payments.requirementDefinition")) &&
    //   !!context.user.labels?.engagements
    // );
  },
  showSideNavTaxProfileSettings: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("*");
  },
  showSideNavTaxDocumentsSettings: (
    context: DashboardFocusedFeatureContext
  ) => {
    return context.access.hasScope("*");
  },
  showSideNavNotificationsSettings: (
    context: DashboardFocusedFeatureContext
  ) => {
    return context.access.hasScope("*");
  },
  showSideNavTeamsSettings: (context: DashboardFocusedFeatureContext) => {
    return context.access.hasScope("*");
  },
  showSideNavEmailSettings: (context: DashboardFocusedFeatureContext) => {
    return (
      context.access.hasScope("*") &&
      Object.keys(context.customization.emailCustomization?.templates ?? {})
        .length > 0
    );
  },
  showTaxFilingYearSwitcher: (context: DashboardFocusedFeatureContext) => {
    return (
      context.access.hasScope("payments.taxForm") &&
      context.user.userType === UserAccountType.enterprise
    );
  },
  showAxisBenefits: (context: DashboardFocusedFeatureContext) => {
    return context.experiments.customBenefitsLloydCapital;
  },
  showBenefits: (context: DashboardFocusedFeatureContext) => {
    if (
      context.experiments.customBenefitsLloydCapital &&
      context.user.payersCount < 2
    ) {
      return false;
    }

    return true;
  }
} satisfies Record<string, CreateDashboardFocusedFeature>;

export type DashboardFocusedFeatureKey =
  keyof typeof DashboardFocusedFeaturesConfig;
